<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PipelineFormDialog from './PipelineFormDialog.vue';
import StageFormDialog from './StageFormDialog.vue';
import FeatureGuide from './guide/FeatureGuide.vue';
import { buildApiBase } from './guide/buildApiBase';
import { usePipelineGuide } from './guide/usePipelineGuide';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const pipelines = useMapGetter('pipelines/getPipelines');
const labels = useMapGetter('labels/getLabels');
const accountId = useMapGetter('getCurrentAccountId');

const expandedId = ref(null);

// --- Guia in-app ("Como usar") --------------------------------------------
const isGuideOpen = ref(false);
const apiBase = computed(() =>
  buildApiBase(window.location.origin, accountId.value)
);
// Token da conta (para copiar/enviar requisições no playground do guia).
const currentUser = useMapGetter('getCurrentUser');
const apiToken = computed(() => currentUser.value?.access_token || '');
const guideContent = usePipelineGuide();

const openBoard = id =>
  router.push({
    name: 'pipeline_board',
    params: { accountId: accountId.value, pipelineId: id },
  });

// Resolve o redirect ANTES de renderizar a config (evita flicker): havendo pipeline
// padrão e sem ?manage, vai direto pro board com replace.
const resolving = ref(true);

onMounted(async () => {
  await store.dispatch('pipelines/get');
  store.dispatch('labels/get');
  if (!route.query.manage) {
    const defaultPipeline = pipelines.value.find(
      pipeline => pipeline.is_default
    );
    if (defaultPipeline) {
      router.replace({
        name: 'pipeline_board',
        params: { accountId: accountId.value, pipelineId: defaultPipeline.id },
      });
      return;
    }
  }
  resolving.value = false;
});

const toggleManage = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

// --- Dialog de pipeline (criar / editar) ----------------------------------
// O dialog lê a prop `pipeline` ao abrir; setamos o alvo e aguardamos o nextTick
// para a prop chegar ao componente filho antes do open().
const pipelineDialogRef = ref(null);
const editingPipeline = ref(null);

const openCreatePipeline = async () => {
  editingPipeline.value = null;
  await nextTick();
  pipelineDialogRef.value?.open();
};
const openEditPipeline = async pipeline => {
  editingPipeline.value = pipeline;
  await nextTick();
  pipelineDialogRef.value?.open();
};
const submitPipeline = async payload => {
  try {
    if (payload.id) {
      await store.dispatch('pipelines/update', {
        id: payload.id,
        name: payload.name,
        description: payload.description,
        is_default: payload.is_default,
        webhook_url: payload.webhook_url,
      });
    } else {
      await store.dispatch('pipelines/create', {
        name: payload.name,
        description: payload.description,
        is_default: payload.is_default,
        webhook_url: payload.webhook_url,
      });
    }
    pipelineDialogRef.value?.close();
  } catch (error) {
    useAlert(t('PIPELINES.FORM.ERROR'));
  }
};

// --- Dialog de etapa (criar / editar) -------------------------------------
const stageDialogRef = ref(null);
const editingStage = ref(null);
const stageDialogPipelineId = ref(null);

const openCreateStage = async pipelineId => {
  stageDialogPipelineId.value = pipelineId;
  editingStage.value = null;
  await nextTick();
  stageDialogRef.value?.open();
};
const openEditStage = async (pipelineId, stage) => {
  stageDialogPipelineId.value = pipelineId;
  editingStage.value = stage;
  await nextTick();
  stageDialogRef.value?.open();
};
const submitStage = async payload => {
  const stageData = {
    pipelineId: stageDialogPipelineId.value,
    name: payload.name,
    label_ids: payload.label_ids,
    behavior: payload.behavior,
    wip_limit: payload.wip_limit,
    rotting_days: payload.rotting_days,
    probability: payload.probability,
  };
  try {
    if (payload.id) {
      await store.dispatch('pipelines/updateStage', {
        ...stageData,
        id: payload.id,
      });
    } else {
      await store.dispatch('pipelines/createStage', stageData);
    }
    stageDialogRef.value?.close();
  } catch (error) {
    useAlert(t('PIPELINES.STAGES.SAVE_ERROR'));
  }
};

// --- Exclusão de etapa (com confirmação) ----------------------------------
const deleteStageDialogRef = ref(null);
const pendingDeleteStage = ref(null);
const pendingDeleteStageName = computed(
  () => pendingDeleteStage.value?.stage?.name ?? ''
);
const requestDeleteStage = (pipelineId, stage) => {
  pendingDeleteStage.value = { pipelineId, stage };
  deleteStageDialogRef.value?.open();
};
const confirmDeleteStage = async () => {
  const pending = pendingDeleteStage.value;
  if (!pending) return;
  try {
    await store.dispatch('pipelines/deleteStage', {
      pipelineId: pending.pipelineId,
      id: pending.stage.id,
    });
  } catch (error) {
    useAlert(t('PIPELINES.STAGES.DELETE_ERROR'));
  } finally {
    pendingDeleteStage.value = null;
    deleteStageDialogRef.value?.close();
  }
};

// --- Exclusão de pipeline (com confirmação) -------------------------------
const deletePipelineDialogRef = ref(null);
const pendingDeletePipeline = ref(null);
const pendingDeletePipelineName = computed(
  () => pendingDeletePipeline.value?.name ?? ''
);
const requestDeletePipeline = pipeline => {
  pendingDeletePipeline.value = pipeline;
  deletePipelineDialogRef.value?.open();
};
const confirmDeletePipeline = async () => {
  const pending = pendingDeletePipeline.value;
  if (!pending) return;
  try {
    await store.dispatch('pipelines/delete', pending.id);
    if (expandedId.value === pending.id) expandedId.value = null;
  } catch (error) {
    useAlert(t('PIPELINES.DELETE_ERROR'));
  } finally {
    pendingDeletePipeline.value = null;
    deletePipelineDialogRef.value?.close();
  }
};

// --- Reordenar estágios (otimista; reverte no store em erro) ---------------
const reorderStages = async pipeline => {
  const stageIds = pipeline.stages.map(stage => stage.id);
  try {
    await store.dispatch('pipelines/reorderStages', {
      pipelineId: pipeline.id,
      stageIds,
    });
  } catch (error) {
    useAlert(t('PIPELINES.STAGES.REORDER_ERROR'));
  }
};
</script>

<template>
  <div class="flex overflow-auto flex-col w-full h-full bg-n-background">
    <div v-if="resolving" class="flex flex-1 justify-center items-center">
      <span class="size-6 i-lucide-loader-2 animate-spin text-n-slate-10" />
    </div>
    <header
      v-if="!resolving"
      class="flex flex-wrap gap-3 items-center px-6 py-4 border-b border-n-weak"
    >
      <div class="flex-grow min-w-0">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('PIPELINES.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">{{ t('PIPELINES.SUBTITLE') }}</p>
      </div>
      <Button
        color="blue"
        size="sm"
        icon="i-lucide-plus"
        :label="t('PIPELINES.FORM.SUBMIT')"
        @click="openCreatePipeline"
      />
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-help-circle"
        :label="t('PIPELINES.HELP')"
        @click="isGuideOpen = true"
      />
    </header>

    <div
      v-if="!resolving"
      class="flex flex-col gap-6 p-6 mx-auto w-full max-w-4xl"
    >
      <div
        v-if="!pipelines.length"
        class="flex flex-col gap-3 items-center px-6 py-12 text-center rounded-xl outline outline-1 outline-dashed outline-n-weak"
      >
        <span
          class="grid rounded-full size-12 place-items-center bg-n-alpha-2 text-n-brand"
        >
          <span class="size-6 i-lucide-workflow" />
        </span>
        <p class="text-base font-medium text-n-slate-12">
          {{ t('PIPELINES.EMPTY_STATE.TITLE') }}
        </p>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('PIPELINES.EMPTY_STATE.DESCRIPTION') }}
        </p>
        <Button
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          :label="t('PIPELINES.FORM.SUBMIT')"
          @click="openCreatePipeline"
        />
        <button
          type="button"
          class="text-sm font-medium underline text-n-brand underline-offset-2"
          @click="isGuideOpen = true"
        >
          {{ t('PIPELINES.EMPTY_STATE.LEARN') }}
        </button>
      </div>

      <ul v-else class="flex flex-col gap-3 p-0 m-0 list-none">
        <li
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="flex flex-col gap-3 p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2"
        >
          <div class="flex flex-wrap gap-3 items-center">
            <div class="flex-grow min-w-0">
              <p
                class="flex gap-2 items-center text-sm font-medium text-n-slate-12"
              >
                {{ pipeline.name }}
                <span
                  v-if="pipeline.is_default"
                  class="px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                >
                  {{ t('PIPELINES.DEFAULT') }}
                </span>
              </p>
              <p v-if="pipeline.description" class="text-xs text-n-slate-11">
                {{ pipeline.description }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ pipeline.stages.length }} {{ t('PIPELINES.STAGES.TITLE') }}
              </p>
            </div>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-1"
              @click="openBoard(pipeline.id)"
            >
              {{ t('PIPELINES.OPEN_BOARD') }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-1"
              @click="toggleManage(pipeline.id)"
            >
              {{ t('PIPELINES.MANAGE') }}
            </button>
            <button
              type="button"
              class="flex-shrink-0 size-4 i-lucide-pencil text-n-slate-10 hover:text-n-brand"
              :title="t('PIPELINES.FORM.EDIT')"
              @click="openEditPipeline(pipeline)"
            />
            <button
              type="button"
              class="flex-shrink-0 size-4 i-lucide-trash-2 text-n-slate-10 hover:text-n-ruby-9"
              :title="t('PIPELINES.DELETE')"
              @click="requestDeletePipeline(pipeline)"
            />
          </div>

          <div
            v-if="expandedId === pipeline.id"
            class="flex flex-col gap-3 pt-3 border-t border-n-weak"
          >
            <p v-if="!pipeline.stages.length" class="text-xs text-n-slate-10">
              {{ t('PIPELINES.STAGES.EMPTY') }}
            </p>
            <Draggable
              v-model="pipeline.stages"
              :disabled="pipeline.stages.length < 2"
              item-key="id"
              tag="ul"
              handle=".pipeline-stage-row-handle"
              ghost-class="opacity-40"
              class="flex flex-col gap-2 p-0 m-0 list-none"
              @end="reorderStages(pipeline)"
            >
              <template #item="{ element: stage }">
                <li
                  class="flex flex-wrap gap-2 items-center px-3 py-2 rounded-lg bg-n-background"
                >
                  <span
                    v-if="pipeline.stages.length > 1"
                    class="pipeline-stage-row-handle flex-shrink-0 size-4 i-lucide-grip-vertical cursor-grab text-n-slate-10"
                    :title="t('PIPELINES.STAGES.DRAG')"
                  />
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ stage.name }}
                  </span>
                  <!-- Badge de comportamento (won/lost) -->
                  <span
                    v-if="stage.behavior === 'won'"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md text-n-teal-11 bg-n-teal-3"
                  >
                    <span class="size-3 i-lucide-trophy" />
                    {{ t('PIPELINES.STAGES.BEHAVIOR_WON') }}
                  </span>
                  <span
                    v-else-if="stage.behavior === 'lost'"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md text-n-ruby-11 bg-n-ruby-3"
                  >
                    <span class="size-3 i-lucide-x-circle" />
                    {{ t('PIPELINES.STAGES.BEHAVIOR_LOST') }}
                  </span>
                  <span class="flex flex-wrap flex-grow gap-1">
                    <span
                      v-for="label in stage.labels"
                      :key="label.id"
                      class="px-1.5 py-0.5 text-xs text-white rounded-md"
                      :style="{ backgroundColor: label.color }"
                    >
                      {{ label.title }}
                    </span>
                    <span
                      v-if="!stage.labels.length"
                      class="text-xs text-n-slate-10"
                    >
                      {{ t('PIPELINES.STAGES.NO_LABELS') }}
                    </span>
                  </span>
                  <!-- Config opcional: WIP / rotting / probabilidade -->
                  <span
                    v-if="stage.wip_limit != null"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-md text-n-slate-11 bg-n-alpha-2"
                    :title="t('PIPELINES.STAGES.WIP_LIMIT')"
                  >
                    <span class="size-3 i-lucide-gauge" />
                    {{ stage.wip_limit }}
                  </span>
                  <span
                    v-if="stage.rotting_days != null"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-md text-n-slate-11 bg-n-alpha-2"
                    :title="t('PIPELINES.STAGES.ROTTING_DAYS')"
                  >
                    <span class="size-3 i-lucide-flame" />
                    {{
                      t('PIPELINES.STAGES.ROTTING_BADGE', {
                        days: stage.rotting_days,
                      })
                    }}
                  </span>
                  <span
                    v-if="stage.probability != null"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-md text-n-slate-11 bg-n-alpha-2"
                    :title="t('PIPELINES.STAGES.PROBABILITY')"
                  >
                    <span class="size-3 i-lucide-percent" />
                    {{ `${stage.probability}%` }}
                  </span>
                  <button
                    type="button"
                    class="flex-shrink-0 size-4 i-lucide-pencil text-n-slate-10 hover:text-n-brand"
                    :title="t('PIPELINES.STAGES.EDIT')"
                    @click="openEditStage(pipeline.id, stage)"
                  />
                  <button
                    type="button"
                    class="flex-shrink-0 size-4 i-lucide-x text-n-slate-10 hover:text-n-ruby-9"
                    :title="t('PIPELINES.DELETE')"
                    @click="requestDeleteStage(pipeline.id, stage)"
                  />
                </li>
              </template>
            </Draggable>

            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-plus"
              class="self-start"
              :label="t('PIPELINES.STAGES.CREATE_TITLE')"
              @click="openCreateStage(pipeline.id)"
            />
          </div>
        </li>
      </ul>
    </div>

    <!-- Modais de criação/edição (UX rica: ícones, labels, hints, tooltips) -->
    <PipelineFormDialog
      ref="pipelineDialogRef"
      :pipeline="editingPipeline"
      @submit="submitPipeline"
    />
    <StageFormDialog
      ref="stageDialogRef"
      :stage="editingStage"
      :labels="labels"
      @submit="submitStage"
    />

    <FeatureGuide
      :open="isGuideOpen"
      :title="t('PIPELINES.TITLE')"
      icon="i-lucide-workflow"
      :api-base="apiBase"
      :api-token="apiToken"
      :content="guideContent"
      @close="isGuideOpen = false"
    />

    <!-- Confirmação de exclusão de etapa (não exclui direto) -->
    <Dialog
      ref="deleteStageDialogRef"
      type="alert"
      :title="t('PIPELINES.STAGES.DELETE_CONFIRM.TITLE')"
      :description="
        t('PIPELINES.STAGES.DELETE_CONFIRM.DESCRIPTION', {
          name: pendingDeleteStageName,
        })
      "
      :confirm-button-label="t('PIPELINES.STAGES.DELETE_CONFIRM.CONFIRM')"
      @confirm="confirmDeleteStage"
    />

    <!-- Confirmação de exclusão de pipeline (não exclui direto) -->
    <Dialog
      ref="deletePipelineDialogRef"
      type="alert"
      :title="t('PIPELINES.DELETE_CONFIRM.TITLE')"
      :description="
        t('PIPELINES.DELETE_CONFIRM.DESCRIPTION', {
          name: pendingDeletePipelineName,
        })
      "
      :confirm-button-label="t('PIPELINES.DELETE_CONFIRM.CONFIRM')"
      @confirm="confirmDeletePipeline"
    />
  </div>
</template>
